if (!window.Eurus.loadedScript.includes('product-cart.js')) {
  window.Eurus.loadedScript.push('product-cart.js');

  // Shopify product forms include `<input name="id">`, which shadows `form.id`.
  const getFormDomId = (formEl) => {
    if (!formEl || typeof formEl.getAttribute !== 'function') return '';
    return formEl.getAttribute('id') || '';
  };

  const normalizeProductOption = (value) =>
    value
      ?.toString()
      .trim()
      .replace(/&quot;/g, '"')
      .normalize('NFKC');

  const resolveVariantIdFromPicker = (formEl) => {
    if (!formEl) return null;

    const contextRoot =
      formEl.closest('.product-info, .card-product, #choose-options-content, .main-product, .choose-options-content') ||
      document;
    const formId = getFormDomId(formEl);
    const horizontalChecked = formId
      ? contextRoot.querySelector(`.variant-option-horizontal input[type="radio"][form="${formId}"]:checked`)
      : contextRoot.querySelector('.variant-option-horizontal input[type="radio"]:checked');
    if (!horizontalChecked) return null;

    const pickerRoot = contextRoot.querySelector('.x-variant-picker, .variant-selects') || contextRoot;
    const variantsScript = pickerRoot.querySelector('script[type="application/json"]');
    if (!variantsScript?.textContent) return null;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (_) {
      return null;
    }
    if (!Array.isArray(variants) || !variants.length) return null;

    const optionPos = parseInt(horizontalChecked.getAttribute('option-pos'), 10);
    const selectedByPos = {};
    pickerRoot.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      const pos = parseInt(input.getAttribute('option-pos'), 10);
      if (pos) selectedByPos[pos] = input.value;
    });

    const matched = variants.find((variant) => {
      for (let p = 1; p <= 3; p += 1) {
        const variantOpt = variant[`option${p}`];
        if (!variantOpt) continue;
        const expected = p === optionPos ? horizontalChecked.value : selectedByPos[p];
        if (!expected) continue;
        if (normalizeProductOption(variantOpt) !== normalizeProductOption(expected)) return false;
      }
      return true;
    });

    return matched?.id ? String(matched.id) : null;
  };

  // Instant quantity UI: sync input before Alpine flush, press feedback, max disabled state.
  if (!window.__qtySelectorUiInstalled) {
    window.__qtySelectorUiInstalled = true;

    const injectQtyBtnStyles = () => {
      let style = document.getElementById('qty-selector-ui-styles');
      if (!style) {
        style = document.createElement('style');
        style.id = 'qty-selector-ui-styles';
        document.head.appendChild(style);
      }
      style.textContent = `
        .quantity-selector-container .qty-btn{
          min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;
          color:inherit!important;
          transition:background-color 75ms ease,opacity 75ms ease;touch-action:manipulation;-webkit-tap-highlight-color:transparent;
        }
        .quantity-selector-container .qty-btn svg{color:inherit;fill:currentColor}
        .quantity-selector-container .qty-btn--pressed{background-color:rgba(var(--text-color,71,63,58),0.1)!important}
        .quantity-selector-container .qty-btn:disabled,
        .quantity-selector-container .qty-btn--disabled{opacity:.3!important;cursor:not-allowed!important;pointer-events:none}
        .quantity-selector-container input[name="quantity"]{
          transform-origin:center center;
        }
        .quantity-selector-container input[name="quantity"].qty-input--pulse{
          animation:qty-input-pulse 200ms ease-out;
        }
        @keyframes qty-input-pulse{
          0%{transform:scale(1)}
          35%{transform:scale(1.1)}
          100%{transform:scale(1)}
        }
        @media (prefers-reduced-motion:reduce){
          .quantity-selector-container input[name="quantity"].qty-input--pulse{animation:none}
        }
      `;
    };

    const getAlpineDataQuick = (containerEl) => {
      if (!containerEl || !window.Alpine) return null;
      try {
        if (typeof window.Alpine.$data === 'function') return window.Alpine.$data(containerEl);
      } catch (_) {}
      return containerEl?._x_dataStack?.[0] || null;
    };

    const resolveMaxQty = (alpineData, input) => {
      const candidates = [];
      if (alpineData?.max != null && alpineData.max !== '') {
        const n = Number(alpineData.max);
        if (Number.isFinite(n)) candidates.push(n);
      }
      if (alpineData?.hasSampleLimit && alpineData.sampleLimit != null) {
        const n = Number(alpineData.sampleLimit);
        if (Number.isFinite(n)) candidates.push(n);
      }
      const dataLimit = input?.dataset?.limit;
      if (dataLimit) {
        const n = Number(dataLimit);
        if (Number.isFinite(n)) candidates.push(n);
      }
      return candidates.length ? Math.min(...candidates) : null;
    };

    const getPairedQuantityInputId = (inputId) => {
      if (!inputId) return null;
      if (inputId.startsWith('Quantity-sticky-')) {
        return 'Quantity-' + inputId.slice('Quantity-sticky-'.length);
      }
      if (inputId.startsWith('Quantity-')) {
        return 'Quantity-sticky-' + inputId.slice('Quantity-'.length);
      }
      return null;
    };

    const enhanceQuantitySelector = (container) => {
      if (!container) return;
      const alpineData = getAlpineDataQuick(container);
      if (!alpineData || typeof alpineData.plus !== 'function') return;

      const input = container.querySelector('input[name="quantity"]');
      const plusBtn = container.querySelector('button[name="plus"], [name="plus"]');
      const minusBtn = container.querySelector('button[name="minus"], [name="minus"]');

      const syncInputNow = () => {
        if (!input || alpineData.qty === '' || alpineData.qty == null) return;
        const next = String(alpineData.qty);
        if (input.value !== next) input.value = next;
      };

      const pulseQtyInput = () => {
        if (!input) return;
        if (typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }
        input.classList.remove('qty-input--pulse');
        void input.offsetWidth;
        input.classList.add('qty-input--pulse');
        const clearPulse = () => input.classList.remove('qty-input--pulse');
        input.addEventListener('animationend', clearPulse, { once: true });
      };

      const updateButtonState = () => {
        const qty = parseInt(alpineData.qty, 10);
        const min = Number.isFinite(Number(alpineData.min)) ? Number(alpineData.min) : 1;
        const max = resolveMaxQty(alpineData, input);
        if (plusBtn) {
          const atMax = max != null && Number.isFinite(qty) && qty >= max;
          plusBtn.disabled = atMax;
          plusBtn.setAttribute('aria-disabled', atMax ? 'true' : 'false');
          plusBtn.classList.toggle('qty-btn--disabled', atMax);
          plusBtn.setAttribute('aria-label', 'plus');
          plusBtn.setAttribute('type', 'button');
        }
        if (minusBtn) {
          const atMin = Number.isFinite(qty) && qty <= min;
          minusBtn.disabled = atMin;
          minusBtn.setAttribute('aria-disabled', atMin ? 'true' : 'false');
          minusBtn.classList.toggle('qty-btn--disabled', atMin);
          minusBtn.setAttribute('type', 'button');
        }
      };

      // Already enhanced: only refresh disabled/enabled state after variant/qty changes.
      if (container.dataset.qtyUiEnhanced === '1') {
        container.__qtyUpdateButtonState = updateButtonState;
        syncInputNow();
        updateButtonState();
        return;
      }
      container.dataset.qtyUiEnhanced = '1';
      injectQtyBtnStyles();

      if (input && input.closest('[id^="sticky-add-to-cart"]') && input.dataset.qtyFocusGuard !== '1') {
        input.dataset.qtyFocusGuard = '1';
        input.setAttribute('readonly', 'readonly');
        const unlock = () => input.removeAttribute('readonly');
        input.addEventListener('touchstart', unlock, { passive: true });
        input.addEventListener('mousedown', unlock);
        input.addEventListener('blur', () => {
          input.setAttribute('readonly', 'readonly');
        });
        if (document.activeElement === input) input.blur();
      }

      const wrapMutator = (fnName, { pulse = false } = {}) => {
        if (typeof alpineData[fnName] !== 'function' || alpineData[fnName].__qtyUiWrapped) return;
        const original = alpineData[fnName].bind(alpineData);
        const wrapped = function (...args) {
          const prevQty = alpineData.qty;
          original(...args);
          syncInputNow();
          updateButtonState();
          if (pulse && String(prevQty) !== String(alpineData.qty)) {
            pulseQtyInput();
          }
          broadcastPairedQty();
        };
        wrapped.__qtyUiWrapped = true;
        alpineData[fnName] = wrapped;
      };

      const broadcastPairedQty = () => {
        if (!input?.id || alpineData.__qtyPairSyncing) return;
        const pairId = getPairedQuantityInputId(input.id);
        if (!pairId) return;
        document.dispatchEvent(new CustomEvent('eurus:product-qty-sync', {
          detail: { sourceId: input.id, pairId, qty: alpineData.qty }
        }));
      };

      const applyPairedQty = (nextQty, { pulse = true } = {}) => {
        if (String(alpineData.qty) === String(nextQty)) {
          syncInputNow();
          updateButtonState();
          return;
        }
        alpineData.qty = nextQty;
        syncInputNow();
        updateButtonState();
        if (pulse) pulseQtyInput();
        if (typeof alpineData.syncToCalculator === 'function') {
          alpineData.syncToCalculator();
        }
      };

      wrapMutator('plus', { pulse: true });
      wrapMutator('minus', { pulse: true });
      wrapMutator('onInput');
      wrapMutator('invalid');

      document.addEventListener('eurus:product-qty-sync', (e) => {
        if (!input || e.detail?.pairId !== input.id) return;
        alpineData.__qtyPairSyncing = true;
        applyPairedQty(e.detail.qty);
        alpineData.__qtyPairSyncing = false;
      });

      if (input) {
        input.addEventListener('input', () => {
          requestAnimationFrame(broadcastPairedQty);
        });
        input.addEventListener('change', broadcastPairedQty);
      }

      if (typeof alpineData.syncToCalculator === 'function' && !alpineData.syncToCalculator.__qtyUiWrapped) {
        const origSync = alpineData.syncToCalculator.bind(alpineData);
        const deferredSync = function (...args) {
          requestAnimationFrame(() => origSync(...args));
        };
        deferredSync.__qtyUiWrapped = true;
        alpineData.syncToCalculator = deferredSync;
      }

      [plusBtn, minusBtn].forEach((btn) => {
        if (!btn || btn.dataset.qtyPressBound === '1') return;
        btn.dataset.qtyPressBound = '1';
        btn.classList.add('qty-btn');
        const press = () => btn.classList.add('qty-btn--pressed');
        const release = () => btn.classList.remove('qty-btn--pressed');
        btn.addEventListener('pointerdown', press);
        btn.addEventListener('pointerup', release);
        btn.addEventListener('pointerleave', release);
        btn.addEventListener('pointercancel', release);
        btn.addEventListener('blur', release);
      });

      container.__qtyUpdateButtonState = updateButtonState;
      syncInputNow();
      updateButtonState();

      if (input?.id?.startsWith('Quantity-sticky-')) {
        const mainId = getPairedQuantityInputId(input.id);
        const mainInput = mainId ? document.getElementById(mainId) : null;
        const mainQty = parseInt(mainInput?.value, 10);
        if (Number.isInteger(mainQty) && String(alpineData.qty) !== String(mainQty)) {
          alpineData.__qtyPairSyncing = true;
          applyPairedQty(mainQty, { pulse: false });
          alpineData.__qtyPairSyncing = false;
        }
      }
    };

    const enhanceAllQuantitySelectors = () => {
      document.querySelectorAll('.quantity-selector-container').forEach(enhanceQuantitySelector);
    };

    window.Eurus = window.Eurus || {};
    window.Eurus.enhanceQuantitySelectors = enhanceAllQuantitySelectors;
    window.Eurus.refreshQuantitySelectorUi = enhanceAllQuantitySelectors;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', enhanceAllQuantitySelectors);
    } else {
      enhanceAllQuantitySelectors();
    }
    document.addEventListener('alpine:initialized', enhanceAllQuantitySelectors);
    setTimeout(enhanceAllQuantitySelectors, 0);
    setTimeout(enhanceAllQuantitySelectors, 250);
    setTimeout(enhanceAllQuantitySelectors, 1000);
  }

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      // Quantity-option driven limiter:
      // - When `Quantity` option is "Single product" or "Single tile", clamp qty to X.
      // - When `Quantity` option is "Box of 12" (or anything else), remove the clamp.
      // This is intentionally input-level (not cart-total) enforcement.
      window.Eurus = window.Eurus || {};
      if (!window.Eurus.quantityOptionLimiterInstalled) {
        window.Eurus.quantityOptionLimiterInstalled = true;

        const QUANTITY_OPTION_NAME = 'Quantity';
        const COLOR_OPTION_NAME = 'Color';
        const SAMPLE_LIMIT_KEYWORDS = ['single product', 'single tile', 'sample', 'single sheet'];
        const BOX_QUANTITY_KEYWORDS = ['box of', 'pack of'];
        const MOQ_EXEMPT_KEYWORDS = [...SAMPLE_LIMIT_KEYWORDS, 'full sheet'];

        const isSingleQuantityValue = (value) => {
          if (!value) return false;
          const v = String(value).toLowerCase();
          return SAMPLE_LIMIT_KEYWORDS.some((keyword) => v.includes(keyword));
        };

        const isMoqExemptValue = (value) => {
          if (!value) return false;
          const v = String(value).toLowerCase();
          return MOQ_EXEMPT_KEYWORDS.some((keyword) => v.includes(keyword));
        };

        const getFormSampleLimit = (qtyInput) => {
          const formEl = qtyInput?.form || (qtyInput?.getAttribute('form') ? document.getElementById(qtyInput.getAttribute('form')) : null);
          const raw = formEl?.dataset?.sampleLimit ?? formEl?.getAttribute?.('data-sample-limit');
          const parsed = parseInt(raw);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const getFormMinQuantity = (qtyInput) => {
          const formEl = qtyInput?.form || (qtyInput?.getAttribute('form') ? document.getElementById(qtyInput.getAttribute('form')) : null);
          const formId = qtyInput?.getAttribute('form') || (formEl ? formEl.id : null);
          const quantitySelected = getSelectedOptionValueForForm(QUANTITY_OPTION_NAME, formId);
          if (isMoqExemptValue(quantitySelected)) return 1;
          const raw = formEl?.dataset?.minQuantity ?? formEl?.getAttribute?.('data-min-quantity');
          const parsed = parseInt(raw, 10);
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
          const inputMin = parseInt(qtyInput?.min, 10);
          if (Number.isFinite(inputMin) && inputMin > 0) return inputMin;
          return 1;
        };

        const getSelectedOptionValueForForm = (optionName, formId) => {
          // Prefer elements tied to the same form via `form="..."`
          const select = formId ? document.querySelector(`select[name="${optionName}"][form="${formId}"], select[name^="${optionName}-"][form="${formId}"]`) : null;
          if (select) {
            const opt = select.selectedOptions && select.selectedOptions[0];
            if (opt) return (opt.textContent || opt.value || '').trim();
          }

          const radio = formId ? document.querySelector(`input[type="radio"][name="${optionName}"][form="${formId}"]:checked, input[type="radio"][name^="${optionName}-"][form="${formId}"]:checked`) : null;
          if (radio) return radio.value;

          // Fallback: any matching inputs on the page (useful if only one form exists)
          const selectFallback = document.querySelector(`select[name="${optionName}"], select[name^="${optionName}-"]`);
          if (selectFallback) {
            const opt = selectFallback.selectedOptions && selectFallback.selectedOptions[0];
            if (opt) return (opt.textContent || opt.value || '').trim();
          }
          const radioFallback = document.querySelector(`input[type="radio"][name="${optionName}"]:checked, input[type="radio"][name^="${optionName}-"]:checked`);
          if (radioFallback) return radioFallback.value;

          return null;
        };

        const getAlpineData = (containerEl) => {
          if (!containerEl || !window.Alpine) return null;
          if (typeof window.Alpine.$data === 'function') {
            try {
              return window.Alpine.$data(containerEl);
            } catch (e) {
              // ignore
            }
          }
          return containerEl?._x_dataStack?.[0] || null;
        };

        const getProductQuantityInputs = () => {
          return Array.from(document.querySelectorAll('input[name="quantity"][type="number"]')).filter((input) => {
            const formEl = input?.form || (input?.getAttribute('form') ? document.getElementById(input.getAttribute('form')) : null);
            return formEl && formEl.getAttribute('data-type') === 'add-to-cart-form';
          });
        };

        const bindQuantityInputEvents = (qtyInput) => {
          if (qtyInput.dataset.eurusQuantityLimiterBound === '1') return;
          qtyInput.dataset.eurusQuantityLimiterBound = '1';

          qtyInput.addEventListener('input', () => {
            // If the limiter is applying programmatically, avoid warning spam.
            const silent = qtyInput.dataset.eurusQuantityLimiterProgrammatic === '1';
            if (silent) qtyInput.dataset.eurusQuantityLimiterProgrammatic = '0';
            clampQuantityInput(qtyInput, { showWarning: !silent });
          });

          qtyInput.addEventListener('blur', () => {
            clampQuantityInput(qtyInput, { showWarning: true });
          });
        };

        const clampQuantityInput = (qtyInput, { showWarning } = { showWarning: false }) => {
          if (!qtyInput) return;

          const formId = qtyInput.getAttribute('form') || getFormDomId(qtyInput?.form) || null;
          const formEl = qtyInput?.form || (formId ? document.getElementById(formId) : null);
          if (!formEl || formEl.getAttribute('data-type') !== 'add-to-cart-form') return;

          const quantitySelected = getSelectedOptionValueForForm(QUANTITY_OPTION_NAME, formId);
          const limit = getFormSampleLimit(qtyInput);
          const shouldLimit = isSingleQuantityValue(quantitySelected) && Number.isFinite(limit);

          // Update qty + limiter state
          const containerEl = qtyInput.closest('.quantity-selector-container');
          const alpineData = getAlpineData(containerEl);
          const currentRaw = parseInt(qtyInput.value);
          const currentQty = Number.isFinite(currentRaw) ? currentRaw : 1;

          if (shouldLimit) {
            qtyInput.dataset.limit = String(limit);
            qtyInput.min = '1';

            const clampedQty = Math.min(Math.max(1, currentQty), limit);
            const wasOverLimit = currentQty > limit;

            if (alpineData) {
              if ('hasSampleLimit' in alpineData) alpineData.hasSampleLimit = true;
              if ('sampleLimit' in alpineData) alpineData.sampleLimit = limit;
              if ('min' in alpineData) alpineData.min = 1;
              if ('qty' in alpineData) alpineData.qty = clampedQty;
            }

            if (clampedQty !== currentQty) {
              qtyInput.value = String(clampedQty);
              // Keep Alpine calculator/price in sync when possible.
              qtyInput.dataset.eurusQuantityLimiterProgrammatic = '1';
              qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
            }

            if (wasOverLimit && showWarning && window.showSampleLimitWarning) {
              window.showSampleLimitWarning(limit);
            }
          } else {
            // Remove limit state
            qtyInput.removeAttribute('data-limit');
            if (alpineData) {
              if ('hasSampleLimit' in alpineData) alpineData.hasSampleLimit = false;
              if ('sampleLimit' in alpineData) alpineData.sampleLimit = null;
            }

            // Enforce product minimum quantity when not in sample-limit mode
            // (Full Sheet / samples are MOQ-exempt and resolve to 1)
            const minQty = getFormMinQuantity(qtyInput);
            const effectiveMin = minQty > 1 ? minQty : 1;
            qtyInput.min = String(effectiveMin);
            if (alpineData && 'min' in alpineData) alpineData.min = effectiveMin;
            if (effectiveMin > 1 && Number.isFinite(currentQty) && currentQty < effectiveMin) {
              if (alpineData && 'qty' in alpineData) alpineData.qty = effectiveMin;
              qtyInput.value = String(effectiveMin);
              qtyInput.dataset.eurusQuantityLimiterProgrammatic = '1';
              qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
            }

            // If a sample-limit prompt is currently visible, remove it when limiting is no longer active.
            document.querySelectorAll('.prompt').forEach((p) => {
              const text = p?.textContent?.toLowerCase?.() || '';
              if (text.includes('limit online sample orders') || text.includes('sample orders')) {
                p.remove();
              }
            });
          }
        };

        const applyLimiterToAll = () => {
          const qtyInputs = getProductQuantityInputs();
          qtyInputs.forEach((qtyInput) => {
            bindQuantityInputEvents(qtyInput);
            clampQuantityInput(qtyInput, { showWarning: false });
          });
          if (typeof window.Eurus?.enhanceQuantitySelectors === 'function') {
            window.Eurus.enhanceQuantitySelectors();
          }
        };

        let scheduleToken = 0;
        const scheduleApply = () => {
          scheduleToken += 1;
          const token = scheduleToken;

          // Run multiple times because the theme re-renders option/quantity DOM asynchronously.
          [0, 80, 250, 600].forEach((delay) => {
            setTimeout(() => {
              if (token !== scheduleToken) return;
              applyLimiterToAll();
            }, delay);
          });
        };

        const bindOptionChangeListeners = () => {
          document.addEventListener('change', (e) => {
            const t = e.target;
            if (!t) return;
            if (
              t.matches(`select[name="${QUANTITY_OPTION_NAME}"]`) ||
              t.matches(`input[type="radio"][name="${QUANTITY_OPTION_NAME}"]`) ||
              t.matches(`select[name="${COLOR_OPTION_NAME}"]`) ||
              t.matches(`input[type="radio"][name="${COLOR_OPTION_NAME}"]`)
            ) {
              scheduleApply();
            }
          }, true);
        };

        bindOptionChangeListeners();
        // Initial clamp (after DOM settles).
        scheduleApply();
      }

      // Reset cart submission locks when the shopper picks a different variant.
      if (!window.Eurus.variantChangeCartResetInstalled) {
        window.Eurus.variantChangeCartResetInstalled = true;
        document.addEventListener('change', (e) => {
          const target = e.target;
          if (!target?.matches?.('input[type="radio"][form^="product-form-"], select[name][form^="product-form-"]')) {
            return;
          }
          document.querySelectorAll('.button-product[x-data*="xProductCart"], [x-data*="xProductCart"]').forEach((el) => {
            const alpineData = el._x_dataStack?.[0];
            if (!alpineData) return;
            alpineData.isSubmitting = false;
            alpineData.loading = false;
          });
        }, true);
      }

      Alpine.data('xProductCart', (
        wrappringVariantId,
        engravingVariantId,
      ) => ({
        loading: false,
        isSubmitting: false,
        errorMessage: false,
        mainHasError: false,
        buttonSubmit: "",
        error_message_wrapper: {},
        stopAction: false,
        insuranceVariantId: '',
        _resolveProductForm() {
          const refForm = this.$refs?.product_form;
          if (refForm?.tagName === 'FORM') return refForm;
          const scopedForm = this.$el?.querySelector?.('form[data-type="add-to-cart-form"]');
          if (scopedForm) return scopedForm;
          const container = this.$el?.closest?.(
            '.choose-options-content, .choose-options, .card-product, .product-info, .main-product'
          );
          return container?.querySelector?.('form[data-type="add-to-cart-form"]') || null;
        },
        _getHorizontalPickerContext(formEl) {
          if (!formEl) return { checkedRadio: null, label: null };

          const formId = getFormDomId(formEl);
          const contextRoot =
            formEl.closest('.product-info, .card-product, #choose-options-content, .main-product, .choose-options-content') ||
            document;

          const checkedRadio = formId
            ? contextRoot.querySelector(`.variant-option-horizontal input[type="radio"][form="${formId}"]:checked`)
            : contextRoot.querySelector('.variant-option-horizontal input[type="radio"]:checked');
          const label = checkedRadio?.id
            ? contextRoot.querySelector(`label.variant-option-label--horizontal[for="${checkedRadio.id}"]`)
            : null;

          return { checkedRadio, label, contextRoot };
        },
        _syncVariantIdFromPicker(formEl) {
          if (!formEl) return null;

          const idInput = formEl.querySelector('input[name="id"]');
          const pickerVariantId = resolveVariantIdFromPicker(formEl);

          if (pickerVariantId && idInput) {
            idInput.value = String(pickerVariantId);
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
            const { label } = this._getHorizontalPickerContext(formEl);
            if (label) label.dataset.variantId = String(pickerVariantId);
            return pickerVariantId;
          }

          return idInput?.value || null;
        },
        checkSampleLimitBeforeAddToCart(e, required, quickView, sticky) {
          if (this.isSubmitting) {
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.stopImmediatePropagation === 'function') {
              e.stopImmediatePropagation();
            }
            return false;
          }

          this.isSubmitting = true;

          // Prevent the default form submission
          e.preventDefault();
          e.stopPropagation();
          
          // Also prevent immediate propagation
          e.stopImmediatePropagation();
          
          // Check if this is a sample product and quantity exceeds limit
          const productForm = this._resolveProductForm();
          if (!productForm) {
            this.isSubmitting = false;
            return false;
          }

          this._syncVariantIdFromPicker(productForm);

          const isSample = this._validateSampleLimitSelection(productForm);
          const limit = this._getFormSampleLimitValue(productForm);

          if (isSample && Number.isFinite(limit) && limit > 0) {
            const formId = getFormDomId(productForm);
            let qtyInput =
              productForm.querySelector('input[name="quantity"]') ||
              (formId ? document.querySelector(`input[name="quantity"][form="${formId}"]`) : null) ||
              productForm.querySelector('input[id*="Quantity"]') ||
              (formId ? document.querySelector(`#Quantity-${formId.replace(/^product-form-/, '')}`) : null);

            let currentQty = parseInt(qtyInput?.value, 10);
            if (!Number.isFinite(currentQty) || currentQty < 1) currentQty = 1;
            if (qtyInput && currentQty > limit) {
              currentQty = limit;
              qtyInput.value = String(limit);
              const container = qtyInput.closest('.quantity-selector-container');
              const alpineData = container?._x_dataStack?.[0];
              if (alpineData && 'qty' in alpineData) alpineData.qty = limit;
              if (alpineData && 'min' in alpineData) alpineData.min = 1;
            }

            const selectedVariantIdInput = productForm.querySelector('input[name="id"]');
            const selectedVariantId = selectedVariantIdInput ? parseInt(selectedVariantIdInput.value, 10) : null;

            this._checkCartSampleLimit(limit, currentQty, selectedVariantId, () => {
              this.addToCart(e, required, quickView, sticky);
            }, () => {
              if (window.showSampleLimitWarning) {
                window.showSampleLimitWarning(limit);
              } else {
                setTimeout(() => {
                  if (window.showSampleLimitWarning) {
                    window.showSampleLimitWarning(limit);
                  } else {
                    this._showSampleLimitWarningFallback(limit);
                  }
                }, 100);
              }

              this.loading = false;
              this.isSubmitting = false;

              const submitBtn = productForm.querySelector('button[type="submit"]');
              if (submitBtn) {
                submitBtn.disabled = true;
                setTimeout(() => {
                  submitBtn.disabled = false;
                }, 1000);
              }
            });
            return;
          }

          // If no sample limit issue, proceed with normal add to cart
          this.addToCart(e, required, quickView, sticky);
        },
        
        _showSampleLimitWarningFallback(limit) {
          // Remove any existing prompts
          const oldPrompts = document.querySelectorAll('.prompt');
          oldPrompts.forEach(prompt => {
            prompt.remove();
          });
          
          // Show warning below the quantity selector on product page
          let quantityContainer = document.querySelector('.quantity-selector-container') ||
                                 document.querySelector('input[id*="Quantity"]')?.closest('.inline-flex') ||
                                 document.querySelector('input[name="quantity"]')?.closest('.inline-flex') ||
                                 document.querySelector('[x-data*="qty:"]') ||
                                 document.querySelector('.quantity-selector');
          
          if (quantityContainer) {
            // Insert warning after the quantity selector
            const warningMessage = `<div class="prompt"><span class="close">&times;</span>Please limit online sample orders to a maximum of ${limit} pieces. For special requests, reach out to us at (856) 988-1802.</div>`;
            
            quantityContainer.insertAdjacentHTML('afterend', warningMessage);
            
            // Add close button functionality
            const closeButton = document.querySelector('.prompt .close');
            if (closeButton) {
              closeButton.addEventListener('click', function() {
                const prompt = this.closest('.prompt');
                if (prompt) {
                  prompt.remove();
                }
              });
            }
          } else {
            // Fallback to product container if quantity selector not found
            let productContainer = document.querySelector('.product-single__meta') || 
                                  document.querySelector('.product-info') || 
                                  document.querySelector('form[data-type="add-to-cart-form"]');
            
            if (productContainer) {
              const warningMessage = `<div class="prompt"><span class="close">&times;</span>Please limit online sample orders to a maximum of ${limit} pieces. For special requests, reach out to us at (856) 988-1802.</div>`;
              
              productContainer.insertAdjacentHTML('afterbegin', warningMessage);
              
              // Add close button functionality
              const closeButton = productContainer.querySelector('.prompt .close');
              if (closeButton) {
                closeButton.addEventListener('click', function() {
                  const prompt = this.closest('.prompt');
                  if (prompt) {
                    prompt.remove();
                  }
                });
              }
            }
          }
        },
        
        // Check cart for existing sample quantities before allowing add to cart
        _checkCartSampleLimit(limit, newQty, variantId, onSuccess, onFailure) {
          // Fetch current cart data
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              let sampleQty = 0;

              // If we have a variantId, enforce per-variant quantity directly.
              if (variantId) {
                const normalizedVariantId = Number(variantId);
                cart.items.forEach(item => {
                  if (Number(item.variant_id) === normalizedVariantId) {
                    sampleQty += item.quantity;
                  }
                });
              } else {
                cart.items.forEach(item => {
                  if (this._isCartItemSample(item)) {
                    sampleQty += item.quantity;
                  }
                });
              }

              // Check if adding new quantity would exceed limit for this variant or global sample set
              if (sampleQty + newQty > limit) {
                onFailure();
              } else {
                onSuccess();
              }
            })
            .catch(error => {
              // If cart fetch fails, proceed with add to cart (fail-safe)
              onSuccess();
            });
        },
        
        // Check if a cart item is a sample product
        _isCartItemSample(cartItem) {
          // Check variant title
          if (cartItem.variant_title) {
            const variantTitle = cartItem.variant_title.toLowerCase();
            if (variantTitle.includes('single tile') || 
                variantTitle.includes('sample') || 
                variantTitle.includes('single sheet')) {
              return true;
            }
          }
          
          // Check product title
          if (cartItem.product_title) {
            const productTitle = cartItem.product_title.toLowerCase();
            if (productTitle.includes('sample') || 
                productTitle.includes('single tile')) {
              return true;
            }
          }
          
          // Check product handle
          if (cartItem.handle) {
            const productHandle = cartItem.handle.toLowerCase();
            if (productHandle.includes('sample') || 
                productHandle.includes('single-tile')) {
              return true;
            }
          }
          
          return false;
        },

        _getFormSampleLimitValue(formEl) {
          if (!formEl) return null;
          const raw = formEl.dataset?.sampleLimit ?? formEl.getAttribute('data-sample-limit');
          const parsed = parseInt(raw, 10);
          return Number.isFinite(parsed) ? parsed : null;
        },

        _getFormMinQuantity(formEl) {
          if (!formEl) return 1;
          if (this._isFormMinQuantityExempt(formEl)) return 1;

          // Qty inputs outside the form may still carry sample data-limit
          const formId = getFormDomId(formEl);
          const qtyInputs = [
            ...formEl.querySelectorAll('input[name="quantity"]'),
            ...(formId
              ? Array.from(document.querySelectorAll(`input[name="quantity"][form="${formId}"]`))
              : []),
          ];
          if (qtyInputs.some((input) => input.dataset?.limit)) {
            return 1;
          }

          const raw = formEl.dataset?.minQuantity ?? formEl.getAttribute('data-min-quantity');
          const parsed = parseInt(raw, 10);
          if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
          }
          return 1;
        },

        _isFormSampleSelection(formEl) {
          if (!formEl) return false;
          let variantId = null;
          try {
            variantId = new FormData(formEl).get('id');
          } catch (e) {
            variantId = null;
          }
          if (!variantId) {
            variantId =
              formEl?.querySelector?.('input[name="id"]')?.value ||
              formEl?.querySelector?.('select[name="id"]')?.value;
          }
          return this._validateSampleLimitSelection(formEl, variantId);
        },

        _isFormMinQuantityExempt(formEl) {
          if (!formEl) return false;
          if (this._isFormSampleSelection(formEl)) return true;

          const { label: horizontalLabel } = this._getHorizontalPickerContext(formEl);
          const haystacks = [
            horizontalLabel?.textContent,
            this._getSelectedQuantityLabel(formEl),
          ];
          let variantId = null;
          try {
            variantId = new FormData(formEl).get('id');
          } catch (e) {
            variantId = null;
          }
          if (!variantId) {
            variantId =
              formEl?.querySelector?.('input[name="id"]')?.value ||
              formEl?.querySelector?.('select[name="id"]')?.value;
          }
          haystacks.push(this._getVariantTitleForForm(formEl, variantId));

          return haystacks.some((value) => String(value || '').toLowerCase().includes('full sheet'));
        },

        _applyMinQuantityToFormData(formEl, formData) {
          const minQty = this._getFormMinQuantity(formEl);
          if (minQty <= 1) return formData;

          const rawQty = parseInt(formData.get('quantity') || '1', 10);
          const qty = Number.isFinite(rawQty) ? rawQty : 1;
          if (qty < minQty) {
            formData.set('quantity', String(minQty));

            const qtyInputs = [
              ...(formEl.querySelectorAll?.('input[name="quantity"]') || []),
            ];
            const formId = getFormDomId(formEl);
            if (formId) {
              document.querySelectorAll(`input[name="quantity"][form="${formId}"]`).forEach((input) => {
                qtyInputs.push(input);
              });
            }
            qtyInputs.forEach((input) => {
              input.value = String(minQty);
              const container = input.closest('.quantity-selector-container');
              const alpineData = container?._x_dataStack?.[0];
              if (alpineData && 'qty' in alpineData) {
                alpineData.qty = minQty;
              }
            });
          }
          return formData;
        },

        _getSelectedQuantityLabel(formEl) {
          if (!formEl) return '';

          const formId = getFormDomId(formEl);
          const { checkedRadio: horizontalChecked, label: horizontalLabel } = this._getHorizontalPickerContext(formEl);
          if (horizontalChecked) {
            return String(horizontalChecked.value || horizontalLabel?.textContent || '').trim();
          }

          const linkedSelect = formId ? document.querySelector(`select[name="Quantity"][form="${formId}"], select[name^="Quantity-"][form="${formId}"]`) : null;
          if (linkedSelect) {
            const selectedOption = linkedSelect.selectedOptions && linkedSelect.selectedOptions[0];
            return (selectedOption?.textContent || linkedSelect.value || '').trim();
          }

          const linkedRadio = formId ? document.querySelector(`input[type="radio"][name="Quantity"][form="${formId}"]:checked, input[type="radio"][name^="Quantity-"][form="${formId}"]:checked`) : null;
          if (linkedRadio) {
            return String(linkedRadio.value || '').trim();
          }

          const localSelect = formEl.querySelector('select[name="Quantity"], select[name^="Quantity-"]');
          if (localSelect) {
            const selectedOption = localSelect.selectedOptions && localSelect.selectedOptions[0];
            return (selectedOption?.textContent || localSelect.value || '').trim();
          }

          const localRadio = formEl.querySelector('input[type="radio"][name="Quantity"]:checked, input[type="radio"][name^="Quantity-"]:checked');
          if (localRadio) {
            return String(localRadio.value || '').trim();
          }

          // Radios/selects live outside the <form> on PDP (no form= attr) — fall back to page scope.
          const contextRoot =
            formEl.closest('.product-info, .card-product, #choose-options-content, .main-product') ||
            document;
          const scopedSelect = contextRoot.querySelector('select[name="Quantity"], select[name^="Quantity-"]');
          if (scopedSelect) {
            const selectedOption = scopedSelect.selectedOptions && scopedSelect.selectedOptions[0];
            return (selectedOption?.textContent || scopedSelect.value || '').trim();
          }
          const scopedRadio = contextRoot.querySelector(
            'input[type="radio"][name="Quantity"]:checked, input[type="radio"][name^="Quantity-"]:checked'
          );
          if (scopedRadio) {
            return String(scopedRadio.value || '').trim();
          }

          return '';
        },

        _getVariantTitleForForm(formEl, variantId) {
          if (!formEl || !variantId) return '';

          const normalizedVariantId = Number(variantId);
          if (!Number.isFinite(normalizedVariantId)) return '';

          const contextRoot =
            formEl.closest('.card-product, #choose-options-content, .product-info, .main-product') ||
            document;

          const selectedVariantScript = contextRoot.querySelector('script[data-selected-variant]');
          if (selectedVariantScript) {
            try {
              const selectedVariant = JSON.parse(selectedVariantScript.textContent || '{}');
              if (Number(selectedVariant?.id) === normalizedVariantId) {
                return String(selectedVariant?.title || selectedVariant?.variant_title || '').trim();
              }
            } catch (e) {
              // ignore parse errors
            }
          }

          const productDataScript = contextRoot.querySelector('script[data-product-data]');
          if (productDataScript) {
            try {
              const productData = JSON.parse(productDataScript.textContent || '{}');
              const variants = Array.isArray(productData?.variants) ? productData.variants : [];
              const matchedVariant = variants.find((variant) => Number(variant?.id) === normalizedVariantId);
              if (matchedVariant) {
                return String(matchedVariant?.title || matchedVariant?.variant_title || '').trim();
              }
            } catch (e) {
              // ignore parse errors
            }
          }

          // PDP: variants JSON inside .variant-selects (no data-product-data attribute)
          const variantScripts = contextRoot.querySelectorAll(
            '.variant-selects script[type="application/json"], script[type="application/json"]'
          );
          for (const script of variantScripts) {
            if (script.hasAttribute('data-selected-variant') || script.hasAttribute('data-product-data')) {
              continue;
            }
            try {
              const parsed = JSON.parse(script.textContent || 'null');
              const variants = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.variants)
                  ? parsed.variants
                  : null;
              if (!variants) continue;
              const matchedVariant = variants.find((variant) => Number(variant?.id) === normalizedVariantId);
              if (matchedVariant) {
                return String(matchedVariant?.title || matchedVariant?.name || '').trim();
              }
            } catch (e) {
              // keep looking
            }
          }

          const xVariants = contextRoot.querySelector('[x-variants-data]');
          if (xVariants?.getAttribute('x-variants-data')) {
            try {
              const variants = JSON.parse(xVariants.getAttribute('x-variants-data') || '[]');
              if (Array.isArray(variants)) {
                const matchedVariant = variants.find((variant) => Number(variant?.id) === normalizedVariantId);
                if (matchedVariant) {
                  return String(matchedVariant?.title || matchedVariant?.name || '').trim();
                }
              }
            } catch (e) {
              // ignore
            }
          }

          return '';
        },

        _validateSampleLimitSelection(formEl, variantId = null) {
          const sampleLimitKeywords = ['single product', 'single tile', 'sample', 'single sheet'];
          // Full sheet is MOQ-exempt but not a sample — keep it out of sample-limit keywords.
          const boxQuantityKeywords = ['box of', 'pack of'];

          const { label: horizontalLabel } = this._getHorizontalPickerContext(formEl);
          if (horizontalLabel) {
            const labelText = String(horizontalLabel.textContent || '').toLowerCase();
            if (sampleLimitKeywords.some((keyword) => labelText.includes(keyword))) {
              return true;
            }
            if (labelText.includes('full sheet')) {
              return false;
            }
            if (boxQuantityKeywords.some((keyword) => labelText.includes(keyword))) {
              return false;
            }
          }

          // Prefer the actual variant on the form (id) — option labels can lag behind after switches.
          const labelVariantId = resolveVariantIdFromPicker(formEl) || horizontalLabel?.dataset?.variantId || null;
          const resolvedVariantId =
            variantId ||
            labelVariantId ||
            formEl?.querySelector?.('input[name="id"]')?.value ||
            (() => {
              try {
                return new FormData(formEl).get('id');
              } catch (e) {
                return null;
              }
            })();
          const variantTitle = this._getVariantTitleForForm(formEl, resolvedVariantId).toLowerCase();
          if (variantTitle) {
            if (sampleLimitKeywords.some((keyword) => variantTitle.includes(keyword))) {
              return true;
            }
            if (variantTitle.includes('full sheet')) {
              return false;
            }
            if (boxQuantityKeywords.some((keyword) => variantTitle.includes(keyword))) {
              return false;
            }
          }

          const selectedQuantity = this._getSelectedQuantityLabel(formEl).toLowerCase();
          if (selectedQuantity) {
            if (sampleLimitKeywords.some((keyword) => selectedQuantity.includes(keyword))) {
              return true;
            }
            if (selectedQuantity.includes('full sheet')) {
              return false;
            }
            if (boxQuantityKeywords.some((keyword) => selectedQuantity.includes(keyword))) {
              return false;
            }
          }

          return false;
        },

        _applySampleQuantityCapToFormData(formEl, formData) {
          if (!formEl || !formData) return formData;

          const variantId = formData.get('id');
          const formId = getFormDomId(formEl);
          const qtyInputs = [
            ...formEl.querySelectorAll('input[name="quantity"]'),
            ...(formId
              ? Array.from(document.querySelectorAll(`input[name="quantity"][form="${formId}"]`))
              : []),
          ];

          if (!this._validateSampleLimitSelection(formEl, variantId)) {
            return formData;
          }

          const limitFromInput = qtyInputs
            .map((input) => parseInt(input.dataset?.limit, 10))
            .find((n) => Number.isFinite(n) && n > 0);
          const limitRaw = limitFromInput || this._getFormSampleLimitValue(formEl);
          const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : 1;
          const rawQty = parseInt(formData.get('quantity') || '1', 10);
          const qty = Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;
          const capped = Math.min(Math.max(1, qty), limit);

          formData.set('quantity', String(capped));

          qtyInputs.forEach((input) => {
            input.value = String(capped);
            input.min = '1';
            if (limit > 0) {
              input.setAttribute('data-limit', String(limit));
            }
            const container = input.closest('.quantity-selector-container');
            const alpineData = container?._x_dataStack?.[0];
            if (alpineData) {
              if ('qty' in alpineData) alpineData.qty = capped;
              if ('min' in alpineData) alpineData.min = 1;
              if ('hasSampleLimit' in alpineData) alpineData.hasSampleLimit = true;
              if ('sampleLimit' in alpineData) alpineData.sampleLimit = limit;
            }
          });

          if (typeof window.Eurus?.refreshQuantitySelectorUi === 'function') {
            window.Eurus.refreshQuantitySelectorUi();
          }

          return formData;
        },

        async _validateSampleLimitForCurrentForm(formEl, formData) {
          const limit = this._getFormSampleLimitValue(formEl);
          if (!limit) return true;

          const rawVariantId = formData?.get('id') || formEl?.querySelector('input[name="id"]')?.value;
          const parsedVariantId = parseInt(rawVariantId, 10);
          const variantId = Number.isFinite(parsedVariantId) ? parsedVariantId : null;

          if (!this._validateSampleLimitSelection(formEl, variantId)) return true;

          const rawQty = formData?.get('quantity') || formEl?.querySelector('input[name="quantity"]')?.value || 1;
          const parsedQty = parseInt(rawQty, 10);
          const newQty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;

          const canAdd = await new Promise((resolve) => {
            this._checkCartSampleLimit(
              limit,
              newQty,
              variantId,
              () => resolve(true),
              () => resolve(false)
            );
          });

          if (!canAdd && window.showSampleLimitWarning) {
            window.showSampleLimitWarning(limit);
          }

          return canAdd;
        },

        loadInsurance(id) {
          if (this.insuranceVariantId == '') {
            this.insuranceVariantId = id;
          }
        },
        scrollToAtcBtn(btnId) {
          const originalAtcBtn = document.querySelector(`#${btnId}`);
          originalAtcBtn.scrollIntoView({
            behavior: "smooth",
            block: 'center',
            inline: 'center'
          })
        },
        async hashProperties(formData) {
          let result = [];
          for (let [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) {
              if (value instanceof File) {
                result.push(`${value.name}:${value.size}:${value.type}`);
              } else {
                result.push(value);
              }
            }
          }
          result.push(formData.get('id'));
          result.sort();

          let data = result.join('|');

          const encoder = new TextEncoder();
          const dataBuffer = encoder.encode(data);

          const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

          return hashHex;
        },
        async addToCart(e, required, quickView, sticky) {
          if (this.loading) {
            this.isSubmitting = false;
            return false;
          }

          this.loading = true;
          e.preventDefault();

          setTimeout(async () => {
            if (required) {
              var productInfo = this.$el.closest('.product-info');
              if(sticky){
                productInfo = document.querySelector('.product-info');
              }
              if (productInfo) {
                var propertiesInput = productInfo.querySelectorAll(`.customization-picker`);
                this.stopAction = false;
                let scrollStatus = false;
                
                propertiesInput.length && propertiesInput.forEach((input) => {
                  if (input.required && input.value.trim() == '' || input.classList.contains("validate-checkbox")) {
                    input.classList.add("required-picker");
                    this.stopAction = true;
                    if(!scrollStatus){
                      input.parentElement.querySelector('.text-required').scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                      scrollStatus = true;
                    }    
                  }                
                });              
              }
              if (this.stopAction) {
                setTimeout (() => {
                  this.loading = false;
                  this.isSubmitting = false;
                }, 500);
                return true;
              }
            }

            if (this.$refs.engraving_text && engravingVariantId) {
              if (this.$refs.engraving_text.value.trim()) {
                if (!this.$refs.engraving_text.hasAttribute('name')) this.$refs.engraving_text.setAttribute('name', this.$refs.text_area_name.value);
              } else {
                if (this.$refs.engraving_text.hasAttribute('name')) this.$refs.engraving_text.removeAttribute('name');
              }
            }

            var productForm = this._resolveProductForm();
            if (!productForm) {
              this.loading = false;
              this.isSubmitting = false;
              return;
            }
            this._syncVariantIdFromPicker(productForm);
            var edt_element = productForm.querySelector('.hidden.cart-edt-properties');
            if (edt_element) {
              edt_element.value = edt_element.value.replace("time_to_cut_off", Alpine.store('xEstimateDelivery').noti)
            }
            let formData = new FormData(productForm);
            
            // Check if calculator is present and use calculated quantity (box / coverage only)
            const productRoot =
              productForm.closest('.main-product, .product-info, #choose-options-content, .choose-options-content') ||
              productForm;
            const calculatorWrapper = productRoot.querySelector('.calculator-wrapper.active');
            let calculatedQuantity = null;
            const selectedVariantIdForCalc = formData.get('id');
            const isSampleSelection = this._validateSampleLimitSelection(
              productForm,
              selectedVariantIdForCalc
            );
            if (calculatorWrapper && !isSampleSelection) {
              const finalQuantityInput = calculatorWrapper.querySelector('#finalquantity');
              if (finalQuantityInput && finalQuantityInput.value && parseInt(finalQuantityInput.value) > 0) {
                calculatedQuantity = finalQuantityInput.value;
                // Update the quantity in the form data
                formData.set('quantity', calculatedQuantity);
              }
            }

            if (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId && this.$refs.gift_wrapping_id) {              
              await this.hashProperties(formData).then(hashHex => {
                this.$refs.gift_wrapping_id.value = hashHex;
                formData = new FormData(productForm);
              });
            }
            
            // Re-apply calculated quantity after gift wrapping hash (if needed)
            if (calculatedQuantity && !isSampleSelection) {
              formData.set('quantity', calculatedQuantity);
            }

            // Samples: never use MOQ / calculator qty — cap to sample_limit (e.g. 1)
            formData = this._applySampleQuantityCapToFormData(productForm, formData);
            formData = this._applyMinQuantityToFormData(productForm, formData);

            if (!formData.get('quantity')) {
              formData.set('quantity', '1');
            }

            const canProceedWithSampleLimit = await this._validateSampleLimitForCurrentForm(productForm, formData);
            if (!canProceedWithSampleLimit) {
              this.loading = false;
              this.isSubmitting = false;
              return;
            }

            formData.append(
              'sections',
              Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            const newFormData = new FormData();
            for (let [key, value] of formData.entries()) {
              if (value !== '') {
                newFormData.append(key, value);
              }
            }
            await fetch(`${Eurus.cart_add_url}`, {
              method:'POST',
              headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
              body: newFormData
            }).then(reponse => {
              return reponse.json();
            }).then(async (response) => {
              if (response.status == '422') {
                if (typeof response.errors == 'object') {
                  this.error_message_wrapper = response.errors;
                  document.querySelector('.recipient-error-message').classList.remove('hidden');
                } else {
                  this.errorMessage = true;
                  setTimeout(() => {
                    this.errorMessage = false;
                  }, 3000);
                  if(this.$refs.error_message){
                    this.$refs.error_message.textContent = response.description;
                  }
                  if(this.$refs.error_message_mobile){
                    this.$refs.error_message_mobile.textContent = response.description;
                  }
                }
                if (Alpine.store('xMiniCart')) {
                  Alpine.store('xMiniCart').reLoad();
                }
              } else {  
                if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
                  Alpine.store('xCartNoti').setItem(response); 
                }
                let match = document.cookie.match('(^|;)\\s*' + 'eurus_insurance' + '\\s*=\\s*([^;]+)');
                if (
                  (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId) || 
                  (this.$refs.engraving_text && engravingVariantId && this.$refs.engraving_text.value.trim()) || 
                  (this.insuranceVariantId && !localStorage.getItem('insuranceRemoved') && (!match || match[1].trim() === ''))
                ) {
                  let additionalOptionData = {
                    items: [],
                    sections:  Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
                  };
                  if (this.$refs.gift_wrapping_checkbox && this.$refs.gift_wrapping_checkbox.checked && wrappringVariantId && response.properties['_gift_wrapping_id']) {
                    additionalOptionData.items.push(
                      {
                        id: wrappringVariantId,
                        quantity: 1,
                        properties: {
                          "For": response.title,
                          "_key_link": response.properties['_gift_wrapping_id']
                        }
                      }
                    );
                  }
                  if (this.$refs.engraving_text && engravingVariantId && this.$refs.engraving_text.value.trim()) {
                    additionalOptionData.items.push(
                      {
                        id: engravingVariantId,
                        quantity: 1
                      }
                    );
                  }
                  if (this.insuranceVariantId && !localStorage.getItem('insuranceRemoved')) {
                    let match = document.cookie.match('(^|;)\\s*' + 'eurus_insurance' + '\\s*=\\s*([^;]+)');
                    if (!match || match[1].trim() === '') {
                      additionalOptionData.items.push(
                        {
                          id: this.insuranceVariantId,
                          quantity: 1
                        }
                      );
                    }
                  }

                  if (additionalOptionData.items.length !== 0) {
                    await window.fetch('/cart/add.js', {
                      method: 'POST',
                      credentials: 'same-origin',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                      },
                      body: JSON.stringify(additionalOptionData),
                    }).then((response) => {
                      return response.json();
                    }).then(response => {
                      document.querySelector('.recipient-error-message') ? document.querySelector('.recipient-error-message').classList.add('hidden') : '';
                      this.error_message_wrapper = {};
        
                      if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
                        Alpine.store('xQuickView').show = false;
                      }
                      Alpine.store('xPopup').close();
                       
                      if((quickView && Alpine.store('xQuickView').buttonQuickView && Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                        document.dispatchEvent(new CustomEvent("eurus:cart:add-as-bundle"));
                      } else {
                        Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                          const sectionElement = document.querySelector(section.selector);
                          if (sectionElement) {
                            if (response.sections[section.id])
                              sectionElement.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
                          }
                        }));
                        if (!Alpine.store('xCartNoti') || !Alpine.store('xCartNoti').enable) {
                          setTimeout(() => {
                            Alpine.store('xMiniCart').openCart();
                          }, 500);
                          document.dispatchEvent(new CustomEvent("eurus:cart:redirect"));  
                        }  
                        Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span').innerHTML);
                        document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));

                        // Dispatch box:added event for sealer popup (only for non-excluded products)
                        this.dispatchBoxAddedEvent(response);
                      }
                    });  
                  }
                } else {
                  document.querySelector('.recipient-error-message') ? document.querySelector('.recipient-error-message').classList.add('hidden') : '';
                  this.error_message_wrapper = {};
        
                  if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
                    Alpine.store('xQuickView').show = false;
                  }
                  Alpine.store('xPopup').close();
                  
                  if((quickView && Alpine.store('xQuickView').buttonQuickView && Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                    document.dispatchEvent(new CustomEvent("eurus:cart:add-as-bundle"));
                  } else {
                    Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                      const sectionElement = document.querySelector(section.selector);
                      if (sectionElement) {
                        if (response.sections[section.id])
                          sectionElement.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
                      }
                    }));
                    if (!Alpine.store('xCartNoti') || !Alpine.store('xCartNoti').enable) {
                      setTimeout(() => {
                        Alpine.store('xMiniCart').openCart();
                      }, 500);
                    }               
                    Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span').innerHTML);
                    document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
                    document.dispatchEvent(new CustomEvent("eurus:cart:redirect"));

                    // Dispatch box:added event for sealer popup (only for non-excluded products)
                    this.dispatchBoxAddedEvent(response);
                  }
                }
              }
            }).catch((error) => {
              console.error('Error:', error);
            }).finally(() => {
              this.loading = false;
              this.isSubmitting = false;
              if ((quickView && Alpine.store('xQuickView').buttonQuickView && !Alpine.store('xQuickView').buttonQuickView.dataset.addAsBundle) || (!quickView && this.$refs.product_form && !this.$refs.product_form.querySelector('[data-add-as-bundle="true"]'))) {
                if(this.$refs.gift_wrapping_checkbox) this.$refs.gift_wrapping_checkbox.checked = false;
                if(this.$refs.gift_wrapping_id) this.$refs.gift_wrapping_id.value = '';
              }
              document.cookie = `eurus_insurance=${this.insuranceVariantId}; path=/`;  
            })
          }, 0)
        },
        dispatchBoxAddedEvent(response) {
          // Check if the added product should be excluded from sealer popup
          if (this.isExcludedFromSealerPopup(response)) {
            return; // Don't dispatch event for excluded products
          }
          
          // Dispatch the box:added event after a small delay to ensure DOM updates are complete
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent("box:added", {
              detail: {
                product: response,
                timestamp: Date.now()
              }
            }));
          }, 100);
        },
        isExcludedFromSealerPopup(response) {
          // List of excluded product variants (case-insensitive matching)
          const excludedVariants = [
            'single tile',
            'sample',
            'sample o',
            'sample x',
            'sample (2 pcs)',
            'sample (4 pcs)',
            'single sheet',
            'single sheet (1 sqft)',
            'full sheet 1sqft',
            'sample 8x8'
          ];
          
          // Check variant title
          if (response.variant_title) {
            const variantTitle = response.variant_title.toLowerCase();
            for (const excluded of excludedVariants) {
              if (variantTitle.includes(excluded)) {
                return true;
              }
            }
          }
          
          // Check product title
          if (response.title || response.product_title) {
            const productTitle = (response.title || response.product_title).toLowerCase();
            for (const excluded of excludedVariants) {
              if (productTitle.includes(excluded)) {
                return true;
              }
            }
          }
          
          return false;
        }
      }))
    });
  });
}

